import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'urql';

import { CreatorDiscoveryInstagramAccount } from '@frontend/app/hooks';
import { IMessagingContext, MessagingContext } from '@frontend/context/MessagingContext';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { QueryVariables } from '../../types';
import { getInstagramCreator } from '../graphql';
import { useInstagramSimilarCreators } from './use-instagram-similar-creators';
import { useInviteInstagramCreator } from './use-invite-instagram-creator';

// Local storage management for invited creators
const INVITED_CREATORS_KEY = 'aspire.creatorDiscovery.invitedCreators';

const getInvitedCreators = (): Set<string> => {
  try {
    const stored = localStorage.getItem(INVITED_CREATORS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch (error) {
    console.error('Error reading invited creators from localStorage:', error);
    return new Set();
  }
};

const saveInvitedCreators = (invitedCreators: Set<string>): void => {
  try {
    localStorage.setItem(INVITED_CREATORS_KEY, JSON.stringify(Array.from(invitedCreators)));
  } catch (error) {
    console.error('Error saving invited creators to localStorage:', error);
  }
};

const addInvitedCreator = (username: string): void => {
  const invitedCreators = getInvitedCreators();
  invitedCreators.add(username);
  saveInvitedCreators(invitedCreators);
};

const removeInvitedCreator = (username: string): void => {
  const invitedCreators = getInvitedCreators();
  invitedCreators.delete(username);
  saveInvitedCreators(invitedCreators);
};

interface UseInstagramCreatorOverviewProps {
  instagramAccount?: CreatorDiscoveryInstagramAccount;
  creatorUsername: string | undefined | null;
  clientId: string | undefined | null;
  onSheetShouldClose: () => void;
  processCreatorClick: (creator: Pick<InstagramCreator, 'username'>) => void;
  clickedCreators: InstagramCreator[];
  originalCreator: Pick<InstagramCreator, 'username'> | null;
  setOriginalCreator: (creator: Pick<InstagramCreator, 'username'> | null) => void;
  updateUrlParams?: (params: Partial<QueryVariables>) => void;
  handleSearch?: () => void;
  queryVariables?: QueryVariables;
  availableCreators?: InstagramCreator[]; // List of creators from search results
}

export const useInstagramCreatorOverview = ({
  instagramAccount,
  creatorUsername,
  clientId,
  onSheetShouldClose,
  processCreatorClick,
  clickedCreators,
  originalCreator,
  setOriginalCreator,
  updateUrlParams,
  handleSearch,
  queryVariables,
  availableCreators,
}: UseInstagramCreatorOverviewProps) => {
  const { igUserId } = instagramAccount ?? {};
  const messagingContext = useContext<IMessagingContext | undefined>(MessagingContext);

  const [{ data, fetching }] = useQuery({
    query: getInstagramCreator,
    variables: {
      username: creatorUsername,
      igUserId,
      clientId,
    },
    pause: !creatorUsername || !igUserId || !clientId,
  });

  const fetchedCreatorData = data?.getInstagramCreator?.creator as InstagramCreator | null | undefined;

  // Compute initial creator data from available creators if not explicitly provided
  const computedInitialCreatorData = creatorUsername && availableCreators ?
    availableCreators.find((c) => c.username === creatorUsername) : null;

  // Only use fetched data if it matches the current creator username to prevent stale data
  const validFetchedData = fetchedCreatorData && fetchedCreatorData.username === creatorUsername ?
    fetchedCreatorData : null;

  // Use valid fetched data first, then fall back to computed initial data
  const currentCreatorData = validFetchedData || computedInitialCreatorData;

  const {
    creators: similarCreators,
    fetching: isFetchingSimilarCreators,
  } = useInstagramSimilarCreators({
    clientId,
    instagramAccount,
    username: creatorUsername,
    limit: 10,
  });

  const { inviteCreator, isInviting } = useInviteInstagramCreator({
    igUserId,
    clientId,
  });

  // State for tracking invited creators
  const [invitedCreators, setInvitedCreators] = useState<Set<string>>(getInvitedCreators());

  const isOpen = !!creatorUsername && !!igUserId;

  // Check if current creator has been invited
  const isCreatorInvited = currentCreatorData ? invitedCreators.has(currentCreatorData.username) : false;

  // Clean up local storage when creator joins the marketplace
  useEffect(() => {
    if (currentCreatorData?.inCreatorMarketplace && currentCreatorData?.username) {
      // Check if creator is in the invited list and remove if they joined the marketplace
      setInvitedCreators((prev) => {
        if (prev.has(currentCreatorData.username)) {
          // Creator has joined the marketplace, remove from invited list
          removeInvitedCreator(currentCreatorData.username);
          const updated = new Set(prev);
          updated.delete(currentCreatorData.username);
          return updated;
        }
        return prev;
      });
    }
  }, [currentCreatorData]);

  // Manage originalCreator lifecycle
  useEffect(() => {
    // Set originalCreator when overview opens for the first time
    if (isOpen && !originalCreator && creatorUsername && igUserId) {
      setOriginalCreator({ username: creatorUsername });
    }

    // Clear originalCreator when overview closes
    if (!isOpen && originalCreator) {
      setOriginalCreator(null);
    }
  }, [isOpen, originalCreator, creatorUsername, igUserId, setOriginalCreator]);

  // Calculate showReturnButton
  const showReturnButton = Boolean(
    originalCreator &&
    creatorUsername &&
    originalCreator.username !== creatorUsername
  );

  const handleReturnToOriginalCreator = () => {
    if (originalCreator) {
      processCreatorClick({
        username: originalCreator.username,
      });
    }
  };

  const handleInviteCreator = async () => {
    if (!currentCreatorData) {
      console.warn('No creator data available for invitation');
      return;
    }

    if (!messagingContext) {
      console.error('MessagingContext not available');
      return;
    }

    try {
      const result = await inviteCreator({
        username: currentCreatorData.username,
      });

      if (result.success) {
        // Add creator to invited list and update localStorage
        addInvitedCreator(currentCreatorData.username);
        setInvitedCreators((prev) => new Set([...prev, currentCreatorData.username]));

        messagingContext?.showSuccessMessage(
          `Successfully invited @${currentCreatorData.username} to the Instagram Creator Marketplace!`
        );
      } else {
        messagingContext?.showErrorMessage(
          result.error || `Failed to invite @${currentCreatorData.username} to the Instagram Creator Marketplace. Please try again.`
        );
      }
    } catch (error) {
      console.error('Unexpected error during creator invitation:', error);
      messagingContext?.showErrorMessage(
        `An unexpected error occurred while inviting @${currentCreatorData.username}. Please try again.`
      );
    }
  };

  return {
    instagramCreatorOverviewProps: {
      open: isOpen,
      onOpenChange: (sheetOpenState: boolean) => {
        if (!sheetOpenState) {
          onSheetShouldClose();
        }
      },
      creator: currentCreatorData || null,
      isLoading: fetching && !computedInitialCreatorData, // For basic creator info
      isLoadingDetailedData: fetching && !validFetchedData, // For sections that need fresh API data
      isFetchingSimilarCreators,
      similarCreators: similarCreators || [],
      onCreatorChange: processCreatorClick,
      clickedCreators,
      originalCreator,
      onReturnToOriginalCreator: handleReturnToOriginalCreator,
      showReturnButton,
      updateUrlParams,
      handleSearch,
      queryVariables,
      onInviteCreator: handleInviteCreator,
      isInviting,
      isCreatorInvited,
    },
  };
};

export default useInstagramCreatorOverview;
